import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import TimeAgo from './TimeAgo';
import { twMerge } from 'tailwind-merge';

import {
  applyTypography,
  BaseButton,
  useAuth,
  useLoginRequiredModal,
  useT,
} from '@repo/common';
import OptionList from './OptionList';
import AskQuestionModal from '../Qna/Modal/AskQuestionModal';
import AnswerModal from '../Qna/Modal/AnswerModal';
import UserProfileModal from '../Modal/UserProfileModal';

import { ReactComponent as HumanIcon } from '@repo/common/assets/icons/28px/icon_28px_human.svg';
import { ReactComponent as LikeIcon } from '@repo/common/assets/icons/20px/icon_20px_like_outline.svg';
import { ReactComponent as OptionIcon } from '@repo/common/assets/icons/20px/icon_20px_option_filled.svg';
import { ReactComponent as LockIcon } from '@repo/common/assets/icons/20px/icon_20px_lock_outline.svg';
import { ReactComponent as VisibleIcon } from '@repo/common/assets/icons/20px/icon_20px_visible_outline.svg';
import { ReactComponent as PlusIcon } from '@repo/common/assets/icons/20px/icon_20px_plus_outline.svg';

import { addQnaLike, removeQnaLike } from '../../api/qna/qna.api';

/**
 * @example
 * <QuestionCard
 *   title="What's the difference between 아주 정말 진짜?"
 *   tag="Phrases & Expressions"
 *   isAnswered
 *   authorName="Gwen"
 *   authorProfileSrc="https://picsum.photos/200/200?random=1"
 *   answererProfileSrc="https://picsum.photos/200/200?random=3"
 *   postedDate="2025-10-29T13:10:00Z"
 *   answererName="Syeoni"
 *   answererTag="Tutor"
 *   answerText={mockAnswer}
 *   currentUser="Gwen"
 * />
 *
 * option: title, tag, authorName, currentUser, postedDate, answererName, answererTag,
 * answerText, isAnswered, authorProfileSrc, answererProfileSrc
 */

const USER_TYPO_KEY = 'b16Strong';
const POSTED_AGO_AND_ANSWER_TYPO_KEY = 'b16';
const TAG_TYPO_KEY = 'b13Strong';
const VISIBLE_TYPO_KEY = 'b12Strong';
const QUESTION_TYPO_KEY = 'h5';

const Avatar = React.memo(({ profileFile, alt }) => {
  const [loaded, setLoaded] = useState(false);

  if (
    !profileFile ||
    profileFile.includes('null') ||
    profileFile.includes('undefined')
  ) {
    return (
      <HumanIcon className='w-full h-full object-cover text-grayscale-gray06' />
    );
  }

  let finalSrc = profileFile;
  if (profileFile.startsWith('data:image/') && profileFile.includes('http')) {
    finalSrc = profileFile.substring(profileFile.indexOf('http'));
  }

  const isBase64 = finalSrc.startsWith('data:image/');
  const isUrl = finalSrc.startsWith('https') || finalSrc.startsWith('http');

  if (isBase64 || isUrl) {
    return (
      <div className='w-full h-full relative'>
        {!loaded && (
          <HumanIcon className='w-full h-full object-cover text-grayscale-gray06 absolute inset-0' />
        )}
        <img
          src={finalSrc}
          alt={alt || 'avatar'}
          className={`w-full h-full object-cover rounded-full ${
            loaded ? 'opacity-100' : 'opacity-0'
          }`}
          key={finalSrc}
          onLoad={() => setLoaded(true)}
          onError={(e) => {
            e.target.onerror = null;
            e.target.style.display = 'none';
            setLoaded(false);
          }}
        />
      </div>
    );
  }

  return (
    <HumanIcon className='w-full h-full object-cover text-grayscale-gray06' />
  );
});

/**
 * QuestionCard 컴포넌트
 *
 * @component
 * @param {string} [title=""] 질문 제목
 * @param {string} [category=""] 질문에 해당하는 카테고리
 * @param {"card-default" | "card-hover" | "card-active"} [stateProp="card-default"] 카드 상태 스타일 (props 이름 변경)
 * @param {string} [authorName=""] 질문 작성자 이름
 * @param {string} [currentUser=""] 현재 로그인한 사용자 ID
 * @param {string} [postedDate=""] 질문 작성 날짜
 * @param {string} [answererName=""] 답변자 이름
 * @param {string} [answererTag=""] 답변자 태그
 * @param {string} [answerText=""] 답변 내용 미리보기 텍스트
 * @param {boolean} [isAnswered=false] 답변 여부
 * @param {string} [authorProfileSrc=""] 질문 작성자 프로필 이미지 경로
 * @param {string} [answererProfileSrc=""] 답변자 프로필 이미지 경로
 */
const QuestionCard = ({
  state: stateProp = 'card-default',
  authorProfileSrc = '',
  answererProfileSrc = '',
  onClick,
  data,
  userId,
  className,
  isHelpfulClicked: initialHelpfulClicked = false,
  onToggleLike,
  onUpdate,
  searchKeyword,
  useIn,
}) => {
  const navigate = useNavigate();
  const t = useT();
  const { user, isLoggedIn } = useAuth();
  const { showLoginModal } = useLoginRequiredModal();
  const [currentState, setCurrentState] = useState(stateProp);
  const [isHelpfulClicked, setIsHelpfulClicked] = useState(
    initialHelpfulClicked,
  );
  const isMyCard = data.author.id === userId;
  const [showOptionList, setShowOptionList] = useState(false);
  const [isAskModalOpen, setIsAskModalOpen] = useState(false);
  const [isAnswerModalOpen, setIsAnswerModalOpen] = useState(false);
  const [editData, setEditData] = useState(null);
  const [answerData, setAnswerData] = useState(data.qnaAnswer);
  const [isHidden, setIsHidden] = useState(data.hidden);
  const [isOnlyTutor, setIsOnlyTutor] = useState(data.onlyTutor);
  const [showProfileModal, setShowProfileModal] = useState({
    open: false,
    data: null,
  });

  const userRole = user?.role ?? null;
  const isAdmin = userRole === 'A';
  const isAnswered = useMemo(() => answerData !== null, [answerData]);
  const buttonIcon = isAdmin ? isAnswered ? '' : <PlusIcon /> : <LikeIcon />;

  const optionBtnRef = useRef(null);

  const buttonLabel = isAdmin
    ? isAnswered
      ? t('Edit answer', '답변 수정')
      : t('Add answer', '답변 추가')
    : t('Super helpful', '정말 도움돼요');

  const handleMouseEnter = () => setCurrentState('card-hover');
  const handleMouseDown = () => setCurrentState('card-pressed');
  const handleMouseUp = () => setCurrentState('card-hover');
  const handleMouseLeave = () => setCurrentState('card-default');

  const currentCardClasses = useMemo(() => {
    let baseClass = '';
    switch (currentState) {
      case 'card-hover':
        baseClass = 'bg-grayscale-gray00 border-[6px] border-surface-green40';
        break;
      case 'card-pressed':
        baseClass = 'bg-green-light01 border-[6px] border-surface-green40';
        break;
      case 'card-answered':
        baseClass = 'bg-grayscale-gray00 opacity-60 text-text-primary';
        break;
      default:
        baseClass = 'bg-grayscale-gray00';
        break;
    }

    if (isHidden) {
      baseClass = baseClass.replace(
        'bg-grayscale-gray00',
        'bg-grayscale-gray02',
      );
    }

    return baseClass;
  }, [currentState, isHidden]);

  const highlightKeyword = (text, keyword) => {
    if (!keyword) return text;
    const regex = new RegExp(`(${keyword})`, 'gi');
    const parts = text.split(regex);

    return parts.map((part, idx) =>
      regex.test(part) ? (
        <span key={idx} className='text-green-deep04'>
          {part}
        </span>
      ) : (
        part
      ),
    );
  };

  useEffect(() => {
    setIsHelpfulClicked(initialHelpfulClicked);
  }, [initialHelpfulClicked]);

  const handleHelpfulClick = async (e) => {
    e.stopPropagation();

    if (!isLoggedIn) {
      showLoginModal('qna_default_helpful_cta');
      return;
    }

    try {
      if (!isHelpfulClicked) {
        await addQnaLike(data.id, userId);
      } else {
        await removeQnaLike(data.id, userId);
      }

      setIsHelpfulClicked(!isHelpfulClicked);
      onToggleLike?.(data.id, !isHelpfulClicked);
    } catch (error) {
      console.error('좋아요 등록/삭제 실패:', error);
    }
  };

  const handleAnswerUpdated = (updatedAnswer) => {
    setAnswerData(updatedAnswer);
    if (onUpdate) onUpdate(data.id, updatedAnswer);
  };

  useEffect(() => {
    setAnswerData(data.qnaAnswer);
  }, [data.qnaAnswer]);

  const handleAddAnswerClick = async (e) => {
    e.stopPropagation();

    if (isAnswered && isAdmin) {
      setEditData({
        answerId: answerData.id,
        content: answerData.content,
        author: answerData.author,
      });
    } else {
      setEditData(null);
    }

    setIsAnswerModalOpen(true);
  };

  const handleOptionIconClick = (e) => {
    e.stopPropagation();
    setShowOptionList((prev) => !prev);
  };

  const closeAskModal = () => {
    setEditData(null);
    setIsAskModalOpen(false);
    setShowOptionList(false);
  };

  const closeAnswerModal = () => {
    setIsAnswerModalOpen(false);
  };

  const handleCategoryClick = (e) => {
    e.stopPropagation();

    navigate('/community/qna', {
      state: {
        categoryId: data.category.id,
      },
    });
  };

  const AuthorAvatar = useMemo(
    () => <Avatar profileFile={authorProfileSrc} alt='Author avatar' />,
    [authorProfileSrc],
  );

  const AnswererAvatar = useMemo(
    () => <Avatar profileFile={answererProfileSrc} alt='Answerer avatar' />,
    [answererProfileSrc],
  );

  const answerContent =
    isAnswered && answerData ? (
      <div className='pt-6 pb-9 border-t border-grayscale-gray02'>
        <div className='flex items-center'>
          <div className='flex items-center w-full h-11 pb-3'>
            <div
              className='w-[26px] h-[26px] tabletH:w-8 tabletH:h-8 mr-2 flex-shrink-0 cursor-pointer'
              onMouseDown={(e) => e.stopPropagation()}
              onMouseUp={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.stopPropagation();
                setShowProfileModal({
                  open: true,
                  data: data.qnaAnswer.author,
                });
              }}
            >
              {AnswererAvatar}
            </div>
            <span
              className={`author-text ${applyTypography(
                USER_TYPO_KEY,
                answerData?.author?.firstName,
              )} text-grayscale-gray09 mr-2 hover:underline hover:text-grayscale-gray08`}
              onClick={(e) => {
                e.stopPropagation();
                setShowProfileModal({
                  open: true,
                  data: data.qnaAnswer.author,
                });
              }}
            >
              {answerData?.author?.firstName ?? ''}
            </span>
            <BaseButton
              size='s'
              type='text'
              hideIcon
              bgclass='bg-orange-orange01 border border-orange-orange01 rounded-lg px-2'
              textclass={`${applyTypography(
                TAG_TYPO_KEY,
              )} text-orange-orange03`}
              hoverclass='hover:bg-orange-orange01 hover:text-orange-orange03'
              activeclass='active:bg-orange-orange01 active:text-orange-orange03'
              onMouseDown={(e) => {
                e.stopPropagation();
              }}
              onMouseUp={(e) => {
                e.stopPropagation();
              }}
              width='w-fit'
            >
              {answerData?.author?.role === 'A' ? 'Tutor' : ''}
            </BaseButton>
          </div>
        </div>
        <div
          className={`
          ${applyTypography(POSTED_AGO_AND_ANSWER_TYPO_KEY)}
          ${isAnswered ? 'text-text-primary' : 'text-text-placeholder'}
          min-h-[60px] max-h-[180px] px-[18px]
          break-words whitespace-pre-line line-clamp-5 text-left
        `}
        >
          {highlightKeyword(answerData?.content || '', searchKeyword)}
        </div>
      </div>
    ) : (
      <div className='pt-6 pb-[30px] border-t border-grayscale-gray02'>
        <div className='flex items-center'>
          <div
            className={`
            ${applyTypography(POSTED_AGO_AND_ANSWER_TYPO_KEY)}
            text-text-placeholder min-h-[60px] px-[18px] text-left whitespace-pre-line
          `}
          >
            {t(
              `Please hold on...\nTutor's answer is coming 😊`,
              `잠시만 기다려 주세요...\n튜터의 답변이 곧 도착합니다 😊`,
            )}
          </div>
        </div>
      </div>
    );

  return (
    <div
      onClick={(e) => {
        if (showOptionList) {
          e.preventDefault();
          e.stopPropagation();
          return;
        }
        if (isAnswerModalOpen) {
          e.stopPropagation();
          return;
        }
        if (showProfileModal.open) {
          e.stopPropagation();
          return;
        }
        onClick?.();
      }}
      className={twMerge(
        `
        min-h-[200px] max-h-[700px] flex-shrink-0
        rounded-[30px] border-[1.6px] border-grayscale-gray02 
        soft-70 
        p-6 
        transition-all duration-200 ease-in-out 
        shadow-sm cursor-pointer
        relative 
        overflow-visible
        ${currentCardClasses}
      `,
        className,
      )}
      onMouseEnter={handleMouseEnter}
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseLeave}
    >
      <div className='flex justify-between items-center'>
        <div className='flex items-center w-full h-12 pb-3'>
          <div
            className='w-[26px] h-[26px] tabletH:w-8 tabletH:h-8 mr-2 flex-shrink-0 rounded-full cursor-pointer'
            onMouseDown={(e) => e.stopPropagation()}
            onMouseUp={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation();
              setShowProfileModal({ open: true, data: data.author });
            }}
          >
            {AuthorAvatar}
          </div>
          <span
            className={`
              ${applyTypography(USER_TYPO_KEY, data.author.firstName)} 
              text-grayscale-gray09 mr-2 cursor-pointer 
              hover:underline hover:text-grayscale-gray08 
              active:text-grayscale-gray07
            `}
            onMouseDown={(e) => {
              e.stopPropagation();
            }}
            onMouseUp={(e) => {
              e.stopPropagation();
            }}
            onClick={(e) => {
              e.stopPropagation();
              setShowProfileModal({ open: true, data: data.author });
            }}
          >
            {data.author.firstName}
          </span>
          <span
            className={`${applyTypography(
              POSTED_AGO_AND_ANSWER_TYPO_KEY,
            )} text-grayscale-gray05`}
          >
            <TimeAgo timestamp={data.createdAt} />
          </span>

          <div className='relative w-10 h-10 ml-auto flex justify-center items-center'>
            <BaseButton
              ref={optionBtnRef}
              type='text'
              size='l'
              icon={<OptionIcon />}
              bgclass={
                isHidden
                  ? 'bg-grayscale-gray02 border-none'
                  : 'bg-grayscale-gray00 border-none'
              }
              textclass={twMerge(
                'text-icon-placeholder',
                showOptionList && 'text-icon-primary',
              )}
              hoverclass={
                isHidden
                  ? 'hover:bg-grayscale-gray02 hover:text-icon-primary'
                  : 'hover:bg-grayscale-gray00 hover:text-icon-primary'
              }
              activeclass={'active:bg-green-light01 active:text-icon-primary'}
              onClick={handleOptionIconClick}
            />
            {showOptionList && (
              <OptionList
                anchorRect={optionBtnRef.current.getBoundingClientRect()}
                isMyCard={isMyCard}
                onClose={() => setShowOptionList(false)}
                onDeleteSuccess={() => {
                  navigate('/community/qna', {
                    state: { refresh: true },
                  });
                }}
                data={data}
                isOnlyTutor={isOnlyTutor}
                onUpdate={(type, payload) => {
                  if (type === 'answer') {
                    setAnswerData(payload);
                  }
                  if (type === 'hidden') {
                    setIsHidden(payload);
                  }
                  if (type === 'onlyTutor') {
                    setIsOnlyTutor(payload);
                  }
                }}
              />
            )}
            <AskQuestionModal
              isOpen={isAskModalOpen}
              onClose={closeAskModal}
              initialData={editData}
            />
          </div>
        </div>
      </div>
      <div className='px-[18px] pb-6 text-left'>
        {data.showCategory && data.category && (
          <BaseButton
            size='s'
            type='text'
            hideIcon
            bgclass='bg-purple-purple01 border border-purple-purple01 rounded-lg px-3 py-[6px] rounded-lg'
            textclass={`${applyTypography(TAG_TYPO_KEY)} text-purple-purple04`}
            hoverclass='hover:bg-purple-purple01 hover:text-purple-purple04'
            activeclass='active:bg-purple-purple02 active:text-purple-purple04'
            width='w-fit'
            onMouseDown={(e) => e.stopPropagation()}
            onMouseUp={(e) => e.stopPropagation()}
            onClick={handleCategoryClick}
          >
            {data.category.name}
          </BaseButton>
        )}

        <div
          className={`
            ${applyTypography(QUESTION_TYPO_KEY, data.title)}
            text-grayscale-gray09 
            break-words 
            whitespace-normal 
            line-clamp-4 
            mt-3
          `}
        >
          {useIn === 'QnaSearch'
            ? highlightKeyword(data.title, searchKeyword)
            : data.title}
        </div>

        {isMyCard && (
          <span
            className={`
              ${applyTypography(VISIBLE_TYPO_KEY)}
              flex items-center text-grayscale-gray05 cursor-pointer mt-3
            `}
          >
            {isOnlyTutor ? (
              <LockIcon className='text-grayscale-gray05 mr-0.5 -mt-0.5 w-5 h-5' />
            ) : (
              <VisibleIcon className='text-icon-primary mr-0.5 -mt-0.5 w-5 h-5' />
            )}
            <span>
              {isOnlyTutor ? 'Visible to tutor only' : 'Visible to everyone'}
            </span>
          </span>
        )}
      </div>
      {answerContent}
      <div>
        <BaseButton
          size='l'
          type='text_basic'
          icon={buttonIcon}
          iconPosition='before'
          onMouseDown={(e) => {
            e.stopPropagation();
          }}
          onMouseUp={(e) => {
            e.stopPropagation();
          }}
          onClick={isAdmin ? handleAddAnswerClick : handleHelpfulClick}
          width='100%'
          textWrapperClass='flex-none'
          bgclass={isHelpfulClicked ? 'bg-green-light01 border-none' : ''}
          textclass={isHelpfulClicked ? 'text-green-deep03' : ''}
          hoverclass={isHelpfulClicked ? 'hover:bg-green-light01' : ''}
        >
          {buttonLabel}
        </BaseButton>
      </div>

      {isAnswerModalOpen && (
        <AnswerModal
          isOpen={isAnswerModalOpen}
          onClose={closeAnswerModal}
          qnaId={data.id}
          initialData={editData}
          onAnswerUpdated={handleAnswerUpdated}
        />
      )}

      {showProfileModal.open && (
        <UserProfileModal
          data={showProfileModal.data}
          onClose={() => {
            setShowProfileModal({ open: false, data: null });
            setCurrentState('card-default');
          }}
        />
      )}
    </div>
  );
};

export default QuestionCard;
